import React, { Ref, useCallback, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Theme,
} from '@mui/material';
import {
  OnListEndReachedCallback,
  useOnScrolledUntilBottom,
} from 'spw-shared/hooks/use-on-scrolled-until-bottom';
import { SxProps } from '@mui/system';
import { Column, SortBy, SortingDirection } from './types';
import {
  BorderlessCell,
  ClickableRow,
  ScrollableTableContainer,
  StyledTableRow,
} from './styles';
import SortButton from './sort-button';

type Props<T> = {
  alternativeContent?: JSX.Element;
  containerRef?: Ref<HTMLDivElement>;
  containerClassName?: string;
  columns: Column<T>[];
  items: T[];
  loading?: boolean;
  onScrolledUntilBottom?: OnListEndReachedCallback;
  onSortChange: (newSort: SortBy) => void;
  onTableRowClick?: (item: T) => void;
  smallPaddingOnLastColumn?: boolean;
  sortBy?: SortBy;
  disabledItemId?: number;
  itemsPerPage?: number;
  getItemAriaLabel?: (item: T) => string;
  highlightedRowId?: number;
  sx?: SxProps<Theme>;
};

const ScrollableTable = <T extends { id?: number | string }>({
  alternativeContent,
  containerRef,
  containerClassName,
  columns,
  items,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  loading = false,
  onScrolledUntilBottom,
  onSortChange,
  onTableRowClick,
  smallPaddingOnLastColumn = false,
  itemsPerPage = 10,
  sortBy,
  disabledItemId,
  getItemAriaLabel,
  highlightedRowId,
  sx,
}: Props<T>) => {
  const TableRowComponent = onTableRowClick ? ClickableRow : StyledTableRow;

  const handleSortIconClick = useCallback(
    (value: string) => {
      let direction: SortingDirection;

      if (sortBy?.column === value && sortBy?.direction === 'ASC') {
        direction = 'DESC';
      } else {
        direction = 'ASC';
      }

      onSortChange({ column: value, direction });
    },
    [sortBy, onSortChange]
  );

  const handleScroll = useOnScrolledUntilBottom({
    itemsLength: items.length,
    itemsPerPage,
    callback: onScrolledUntilBottom,
  });

  const tableBodyContent = useMemo(() => {
    if (alternativeContent) {
      return (
        <TableRow>
          <BorderlessCell colSpan={columns.length}>
            {alternativeContent}
          </BorderlessCell>
        </TableRow>
      );
    }

    return items.map((item, i) => (
      <TableRowComponent
        className={highlightedRowId === item.id ? 'highlighted' : ''}
        style={{ opacity: item.id === disabledItemId ? 0.2 : 1.0 }}
        key={item.id}
        onClick={() => onTableRowClick?.(item)}
        aria-label={getItemAriaLabel?.(item)}
      >
        {columns.map(
          ({
            value,
            align,
            render,
            divider,
            getTestId,
            getAriaLabel,
            onCellClick,
          }) => (
            <TableCell
              key={value}
              align={align}
              className={divider && 'divider'}
              data-testid={getTestId?.(item, i)}
              aria-label={getAriaLabel?.(item)}
              onClick={onCellClick}
            >
              {render
                ? render(item, i)
                : (item[value as keyof typeof item] as any)}
            </TableCell>
          )
        )}
      </TableRowComponent>
    ));
  }, [
    alternativeContent,
    columns,
    items,
    // loading,
    onTableRowClick,
    disabledItemId,
    getItemAriaLabel,
    highlightedRowId,
  ]);

  return (
    <ScrollableTableContainer
      ref={containerRef}
      className={containerClassName}
      onScroll={handleScroll}
      $smallPaddingOnLastColumn={smallPaddingOnLastColumn}
    >
      <Table sx={sx} stickyHeader style={{ willChange: 'transform' }}>
        <TableHead>
          <TableRow>
            {columns.map(
              ({ text, value, sortable, align, divider, renderHeader }) => (
                <TableCell
                  key={value}
                  align={align}
                  className={divider && 'divider'}
                  sortDirection={
                    sortBy?.column === value
                      ? (sortBy.direction.toLowerCase() as 'asc' | 'desc')
                      : false
                  }
                >
                  <div>
                    {renderHeader
                      ? renderHeader(value, sortBy, handleSortIconClick)
                      : text}
                    {sortable && (
                      <SortButton
                        onClick={() => handleSortIconClick(value)}
                        value={value}
                        sortBy={sortBy}
                        testingId={`${text}_sort_button`}
                      />
                    )}
                  </div>
                </TableCell>
              )
            )}
          </TableRow>
        </TableHead>
        <TableBody>{tableBodyContent}</TableBody>
      </Table>
    </ScrollableTableContainer>
  );
};

export default ScrollableTable;
